import OnTheGoGuestMenu from './pages/OnTheGoGuestMenu';
import OnTheGoGuestRatings from './pages/OnTheGoGuestRatings';
import OnTheGoPublicReview from './pages/OnTheGoPublicReview';
import OwnerPaymentAlerts from './components/OwnerPaymentAlerts';
import SageDesk from './pages/mobilev2/sage/SageDesk';
import React, { Suspense, useEffect, useState } from "react";
import {verifiedMode} from './lib/workspaceMode';
import ModeNotice from './components/ModeNotice';
const OnTheGoPOS = React.lazy(() => import('./pages/OnTheGoPOS'));
const CustomerPayment = React.lazy(()=>import('./pages/CustomerPayment'));
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "sonner";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { OutletProvider } from "@/context/OutletContext";
import { DataScopeProvider } from "@/context/DataScopeContext";
import { ThemeProvider } from "@/context/ThemeContext";
import useDocumentTitle from "@/lib/useDocumentTitle";
import { installTabScroll } from "@/lib/tabScrollIntoView";
import ProtectedRoute from "@/components/ProtectedRoute";
import RouteLoadBoundary, { RouteLoading } from "@/components/RouteLoadBoundary";
const Layout = React.lazy(() => import("@/components/Layout"));
const LiveStatusPill = React.lazy(() => import("@/components/LiveStatusPill"));
const SageNudgeListener = React.lazy(() => import("@/components/SageNudgeListener"));
const Login = React.lazy(() => import("@/pages/Login"));
const Register = React.lazy(() => import("@/pages/Register"));
const CommandCentre = React.lazy(() => import("@/pages/CommandCentre"));
const ReportsDeck = React.lazy(() => import("@/pages/ReportsDeck"));
const SageInbox = React.lazy(() => import("@/pages/SageInbox"));
const PresentationEngine = React.lazy(() => import("@/pages/PresentationEngine"));
const MobileHomeV2 = React.lazy(() => import("@/pages/mobilev2/Home"));
const MobileWork = React.lazy(() => import("@/pages/mobilev2/Work"));
const MobileOperations = React.lazy(() => import("@/pages/mobilev2/Operations"));
const MobileInsights = React.lazy(() => import("@/pages/mobilev2/Insights"));
const MobileMore = React.lazy(() => import("@/pages/mobilev2/More"));
const MobileNotifications = React.lazy(() => import("@/pages/mobilev2/Notifications"));
const NewFoodTrialMobile = React.lazy(() => import("@/pages/mobilev2/food_trial/NewTrial"));
const InventoryRoute = React.lazy(() => import("@/pages/mobilev2/stock/InventoryRoute"));
const RapidStockCount = React.lazy(() => import("@/pages/mobilev2/stock/RapidCount"));
const KitchenMode = React.lazy(() => import("@/pages/mobilev2/recipes/KitchenMode"));
const PayablesDesk = React.lazy(() => import("@/pages/mobilev2/purchase/PayablesDesk"));
const POWizardMobile = React.lazy(() => import("@/pages/mobilev2/purchase/POWizard"));
const ReceiveStockMobile = React.lazy(() => import("@/pages/mobilev2/purchase/Receive"));
const PrepBoardMobile = React.lazy(() => import("@/pages/mobilev2/production/PrepBoard"));
const TestDemoPurge = React.lazy(() => import("@/pages/admin/TestDemoPurge"));
const MobilePurchase = React.lazy(() => import("@/pages/mobile/MobilePages").then((module) => ({ default: module.MobilePurchase })));

// HomeGate — root path picks the operational cockpit that fits the screen.
// Phase 181.70 · Desktop → Command Centre; mobile → new Mobile Home v2
// (Chef Command Center); rider role has its own PWA branch downstream.
function SageRoute() {
  const mobile = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 767px)").matches;
  return mobile ? <SageDesk /> : <SageInbox />;
}

function HomeGate() {
  const isMobile = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 767px)").matches;
  return isMobile ? <MobileHomeV2 /> : <Navigate to="/command" replace />;
}
const Recipes = React.lazy(() => import("@/pages/Recipes"));
const RecipePrint = React.lazy(() => import("@/pages/RecipePrint"));
const PrepRecipes = React.lazy(() => import("@/pages/PrepRecipes"));
const Ingredients = React.lazy(() => import("@/pages/Ingredients"));
const NutritionLedger = React.lazy(() => import("@/pages/NutritionLedger"));
const MenuStudio = React.lazy(() => import("@/pages/MenuStudio"));
const WasteLog = React.lazy(() => import("@/pages/WasteLog"));
const FoodTrials = React.lazy(() => import("@/pages/FoodTrials"));
const PublicMenu = React.lazy(() => import("@/pages/PublicMenu"));
const Simulator = React.lazy(() => import("@/pages/Simulator"));
const History = React.lazy(() => import("@/pages/History"));
const MenuEngineering = React.lazy(() => import("@/pages/MenuEngineering"));
const Integrations = React.lazy(() => import("@/pages/Integrations"));
const BackupRecovery = React.lazy(() => import("@/pages/BackupRecovery"));
const DeploymentGate = React.lazy(() => import("@/pages/DeploymentGate"));
const MenuEngineering2 = React.lazy(() => import("@/pages/MenuEngineering2"));
const Suppliers = React.lazy(() => import("@/pages/Suppliers"));
const PurchaseCommandCentre = React.lazy(() => import("@/pages/purchase/PurchaseCommandCentre"));
const PurchaseOrderList = React.lazy(() => import("@/pages/purchase/PurchaseOrderList"));
const CreatePurchaseOrder = React.lazy(() => import("@/pages/purchase/CreatePurchaseOrder"));
const PurchaseOrderDetail = React.lazy(() => import("@/pages/purchase/PurchaseOrderDetail"));
const PurchaseHistory = React.lazy(() => import("@/pages/purchase/PurchaseHistory"));
const Production = React.lazy(() => import("@/pages/Production"));
const POS = React.lazy(() => import("@/pages/POS"));
// Legacy Exhibition POS retired; OnTheGoPOS remains the event POS.
const KDS = React.lazy(() => import("@/pages/KDS"));
const loadOnlineOrder = () => import("@/pages/OnlineOrder");
const OnlineOrder = React.lazy(loadOnlineOrder);
const SelfOrderingKiosk = React.lazy(() => import("@/pages/SelfOrderingKiosk"));
import BrandStorefrontGate from "@/components/BrandStorefrontGate";
const ExpenseRoute = React.lazy(() => import("@/pages/mobilev2/expenses/ExpenseRoute"));
const Expenses = React.lazy(() => import("@/pages/Expenses"));
const Analytics = React.lazy(() => import("@/pages/Analytics"));
const Customers = React.lazy(() => import("@/pages/Customers"));
const Coupons = React.lazy(() => import("@/pages/Coupons"));
const Promotions = React.lazy(() => import("@/pages/Promotions"));
const Orders = React.lazy(() => import("@/pages/Orders"));
const DeliveryZones = React.lazy(() => import("@/pages/DeliveryZones"));
const Intelligence = React.lazy(() => import("@/pages/Intelligence"));
const Users = React.lazy(() => import("@/pages/Users"));
const PrepChefBoard = React.lazy(() => import("@/pages/PrepChefBoard"));
const Academy = React.lazy(() => import("@/pages/Academy"));
const StockCount = React.lazy(() => import("@/pages/StockCount"));
const Outlets = React.lazy(() => import("@/pages/Outlets"));
const ClosingRoute = React.lazy(() => import("@/pages/mobilev2/closing/ClosingRoute"));
const Closing = React.lazy(() => import("@/pages/Closing"));
const Approvals = React.lazy(() => import("@/pages/Approvals"));
const DataImport = React.lazy(() => import("@/pages/DataImport"));
const MarketplaceIntegrations = React.lazy(() => import("@/pages/MarketplaceIntegrations"));
const MarketplaceEvents = React.lazy(() => import("@/pages/MarketplaceEvents"));
const QRCampaigns = React.lazy(() => import("@/pages/QRCampaigns"));
const OrderTrack = React.lazy(() => import("@/pages/OrderTrack"));
const Reviews = React.lazy(() => import("@/pages/Reviews"));
const TelephoneOrder = React.lazy(() => import("@/pages/TelephoneOrder"));
const Rider = React.lazy(() => import("@/pages/Rider"));
const PublicFeedback = React.lazy(() => import("@/pages/PublicFeedback"));
const DeliveryCommand = React.lazy(() => import("@/pages/DeliveryCommand"));
const ChannelPnL = React.lazy(() => import("@/pages/ChannelPnL"));
const MasterData = React.lazy(() => import("@/pages/MasterData"));
const SageDailyPlan = React.lazy(() => import("@/pages/SageDailyPlan"));
const SystemHealth = React.lazy(() => import("@/pages/SystemHealth"));
const Forecast = React.lazy(() => import("@/pages/Forecast"));
const CustomerVoice = React.lazy(() => import("@/pages/CustomerVoice"));
const Connect = React.lazy(() => import("@/pages/Connect"));
const EventLog = React.lazy(() => import("@/pages/EventLog"));
const MoneyHub = React.lazy(() => import("@/pages/MoneyHub"));
const SageActions = React.lazy(() => import("@/pages/SageActions"));
const CustomUnits = React.lazy(() => import("@/pages/CustomUnits"));
const AddonGroups = React.lazy(() => import("@/pages/AddonGroups"));
const Combos = React.lazy(() => import("@/pages/Combos"));
import CostBackgroundManager from "@/components/CostBackgroundManager";
import PwaRouteMeta from "@/components/PwaRouteMeta";
import BrandRuntimeGate from "@/components/BrandRuntimeGate";
import OrderStartupBoundary from "@/components/OrderStartupBoundary";
const LabelStudio = React.lazy(() => import("@/pages/LabelStudio"));
const PrintSettings = React.lazy(() => import("@/pages/PrintSettings"));

function GuestOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function TitleManager() {
  useDocumentTitle();
  return null;
}

export default function App() {
  const [modeKey,setModeKey]=useState(verifiedMode());
  useEffect(()=>{const changed=()=>setModeKey(verifiedMode());window.addEventListener('env:changed',changed);return()=>window.removeEventListener('env:changed',changed);},[]);
  // Phase 132 · Keep the newly-focused field visible when tabbing through forms.
  React.useEffect(() => installTabScroll(), []);
  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
      <BrowserRouter
        basename={window.location.pathname.match(/^\/proxy\/[^/]+/)?.[0] || "/"}
      >
      <OrderStartupBoundary prepareRoute={loadOnlineOrder}>
      <BrandRuntimeGate>
      <ThemeProvider>
      <AuthProvider>
        <DataScopeProvider><OutletProvider>
          <TitleManager />
          <PwaRouteMeta />
          <CostBackgroundManager />
          <OwnerPaymentAlerts />
          <Toaster
            position="top-right"
            richColors
            offset={96}
            toastOptions={{ style: { pointerEvents: "auto" } }}
          />
          <RouteLoadBoundary>
          <Suspense fallback={<RouteLoading />}>
          <Routes key={modeKey}>
            <Route path="/otg-menu/:token" element={<OnTheGoGuestMenu />} />
            <Route path="/otg-ratings/:token" element={<OnTheGoGuestRatings />} />
            <Route path="/otg-review/:token" element={<OnTheGoPublicReview />} />
            <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
            <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
            <Route path="/public/menu" element={<><ModeNotice /><PublicMenu /></>} />
            <Route path="/order" element={<><ModeNotice /><BrandStorefrontGate><OnlineOrder /></BrandStorefrontGate></>} />
            <Route path="/track/:orderNumber" element={<><ModeNotice /><OrderTrack /></>} />
            <Route path="/pay/:orderNumber" element={<><ModeNotice /><CustomerPayment /></>} />
            <Route path="/feedback/:orderId" element={<><ModeNotice /><PublicFeedback /></>} />
            <Route path="/self-order" element={<><ModeNotice /><BrandStorefrontGate><SelfOrderingKiosk /></BrandStorefrontGate></>} />
            <Route path="/rider" element={<ProtectedRoute><><ModeNotice /><Rider /></></ProtectedRoute>} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ModeNotice />
                    <LiveStatusPill />
                    <SageNudgeListener />
                    <Routes>
                      <Route path="/" element={<HomeGate />} />
                      <Route path="/work" element={<MobileWork />} />
                      <Route path="/operations" element={<MobileOperations />} />
                      <Route path="/insights" element={<MobileInsights />} />
                      <Route path="/more" element={<MobileMore />} />
                      <Route path="/notifications" element={<MobileNotifications />} />
                      <Route path="/m/food-trials/new" element={<NewFoodTrialMobile />} />
                      <Route path="/m/inventory" element={<InventoryRoute dedicated />} />
                      <Route path="/m/stock-count" element={<RapidStockCount />} />
                      <Route path="/m/recipes/:id/kitchen" element={<KitchenMode />} />
                      <Route path="/m/prep-recipes/:id/kitchen" element={<KitchenMode />} />
                      <Route path="/m/payables" element={<PayablesDesk />} />
                      <Route path="/m/purchase" element={<MobilePurchase />} />
                      <Route path="/m/purchase/new" element={<POWizardMobile />} />
                      <Route path="/m/purchase/:id/receive" element={<ReceiveStockMobile />} />
                      <Route path="/m/prep-board" element={<PrepBoardMobile />} />
                      <Route path="/admin/test-demo-purge" element={<TestDemoPurge />} />
                      <Route path="/command" element={<CommandCentre />} />
                      <Route path="/reports-deck" element={<ReportsDeck />} />
                      <Route path="/presentations" element={<PresentationEngine />} />
                      <Route path="/m/sage" element={<SageDesk />} />
                      <Route path="/sage" element={<SageRoute />} />
                      <Route path="/prep-recipes" element={<PrepRecipes />} />
                      <Route path="/recipes" element={<Recipes />} />
                      <Route path="/recipes/:id/print" element={<RecipePrint />} />
                      <Route path="/ingredients" element={<InventoryRoute initial="stock"><Ingredients /></InventoryRoute>} />
                      <Route path="/nutrition-ledger" element={<NutritionLedger />} />
                      <Route path="/simulator" element={<Simulator />} />
                      <Route path="/engineering" element={<MenuEngineering />} />
                      <Route path="/integrations" element={<Integrations />} />
                      <Route path="/settings/backup" element={<BackupRecovery />} />
                      <Route path="/settings/deployment-gate" element={<DeploymentGate />} />
                      <Route path="/settings/printers" element={<PrintSettings />} />
                      <Route path="/engineering-2" element={<MenuEngineering2 />} />
                      <Route path="/suppliers" element={<Suppliers />} />
                      {/* Purchase Command Centre — canonical Purchase route (Phase 181.45) */}
                      <Route path="/purchase" element={<PurchaseCommandCentre />} />
                      <Route path="/purchase/list" element={<PurchaseOrderList />} />
                      <Route path="/purchase/create" element={<CreatePurchaseOrder />} />
                      <Route path="/purchase/history" element={<PurchaseHistory />} />
                      <Route path="/purchase/:id" element={<PurchaseOrderDetail />} />
                      {/* Legacy /procurement redirects — single canonical route only */}
                      <Route path="/procurement" element={<Navigate to="/purchase" replace />} />
                      <Route path="/procurement/*" element={<Navigate to="/purchase" replace />} />
                      <Route path="/production" element={<InventoryRoute initial="batches"><Production /></InventoryRoute>} />
                      <Route path="/pos" element={<POS />} />
                      <Route path="/exhibition-pos" element={<Navigate to="/on-the-go-pos" replace />} />
                      <Route path="/on-the-go-pos" element={<OnTheGoPOS />} />
                      <Route path="/kds" element={<KDS />} />
                      <Route path="/expenses" element={<ExpenseRoute><Expenses /></ExpenseRoute>} />
                      <Route path="/m/expenses" element={<ExpenseRoute dedicated />} />
                      <Route path="/analytics" element={<Analytics />} />
                      <Route path="/intelligence" element={<Intelligence />} />
                      <Route path="/team" element={<Users />} />
                      <Route path="/prep-board" element={<PrepChefBoard />} />
                      <Route path="/help" element={<Academy />} />
                      <Route path="/academy" element={<Academy />} />
                      <Route path="/stock-count" element={<InventoryRoute initial="count"><StockCount /></InventoryRoute>} />
                      <Route path="/outlets" element={<Outlets />} />
                      <Route path="/closing" element={<ClosingRoute><Closing /></ClosingRoute>} />
                      <Route path="/m/closing" element={<ClosingRoute dedicated />} />
                      <Route path="/approvals" element={<Approvals />} />
                      <Route path="/import" element={<DataImport />} />
                      <Route path="/marketplace" element={<MarketplaceIntegrations />} />
                      <Route path="/marketplace/events" element={<MarketplaceEvents />} />
                      <Route path="/qr-campaigns" element={<QRCampaigns />} />
                      <Route path="/reviews" element={<Reviews />} />
                      <Route path="/telephone-order" element={<TelephoneOrder />} />
                      <Route path="/customers" element={<Customers />} />
                      <Route path="/coupons" element={<Coupons />} />
                      <Route path="/promotions" element={<Promotions />} />
                      <Route path="/label-studio" element={<LabelStudio />} />
                      <Route path="/orders" element={<Orders />} />
                      <Route path="/delivery-zones" element={<DeliveryZones />} />
                      <Route path="/history" element={<History />} />
                      <Route path="/waste" element={<InventoryRoute initial="waste"><WasteLog /></InventoryRoute>} />
                      <Route path="/food-trials" element={<FoodTrials />} />
                      <Route path="/menu" element={<MenuStudio />} />
                      <Route path="/delivery-command" element={<DeliveryCommand />} />
                      <Route path="/m/delivery" element={<DeliveryCommand dedicated />} />
                      <Route path="/channel-pnl" element={<ChannelPnL />} />
                      <Route path="/master-data" element={<MasterData />} />
                      <Route path="/sage-inbox" element={<Navigate replace to="/sage" />} />
                      <Route path="/sage-plan" element={<SageDailyPlan />} />
                      <Route path="/system-health" element={<SystemHealth />} />
                      <Route path="/forecast" element={<Forecast />} />
                      <Route path="/customer-voice" element={<CustomerVoice />} />
                      <Route path="/connect" element={<Connect />} />
                      <Route path="/loop" element={<Navigate replace to="/sage" />} />
                      <Route path="/events" element={<EventLog />} />
                      <Route path="/money" element={<MoneyHub />} />
                      <Route path="/sage-actions" element={<SageActions />} />
                      <Route path="/custom-units" element={<CustomUnits />} />
                      <Route path="/addon-groups" element={<AddonGroups />} />
                      <Route path="/combos" element={<Combos />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
          </Suspense>
          </RouteLoadBoundary>
        </OutletProvider></DataScopeProvider>
      </AuthProvider>
      </ThemeProvider>
      </BrandRuntimeGate>
      </OrderStartupBoundary>
      </BrowserRouter>
      </QueryClientProvider>
    </div>
  );
}
